import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Trash2, Search, Box, Check, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  stockRowsOf, findCatalogProduct, variantLabelOf,
  commitStockRow, deleteStockRow, catalogProductsOf,
  findWarehouse
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { IconAction, RowActions } from '../../components/RowActions';

export const WhPendingStockTab = ({ org, warehouseId, showNotification, canEdit }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [draftAlert, setDraftAlert] = useState({});

  const products = useMemo(() => catalogProductsOf(org.id), [org.id, version, dataVersion]);
  const warehouse = useMemo(() => findWarehouse(warehouseId), [warehouseId, dataVersion]);

  const rows = useMemo(() => {
    let all = stockRowsOf(org.id, warehouseId, 'pending').map(row => {
      const noteStr = (row.note || '').toLowerCase();
      const isTransfer = !!row.note && noteStr.startsWith('from ');
      const isEmergencyTransfer = isTransfer && noteStr.includes('(emergency)');
      const isReserved = (noteStr.includes('emerg') || noteStr.includes('emery')) && !isTransfer;
      return {
        ...row,
        isTransfer,
        isEmergencyTransfer,
        isReserved
      };
    });
    
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((r) => {
      const p = findCatalogProduct(r.productId);
      return p && (p.name || '').toLowerCase().includes(q);
    });
  }, [search, org.id, warehouseId, version, dataVersion]);

  const accent = org.theme.primary;

  const commit = (row) => {
    const alertQty = Number(draftAlert[row.id] ?? row.minStockThreshold) || 0;
    
    commitStockRow(row.id, 0, alertQty);
    
    if (showNotification) showNotification('Moved to active inventory.', 'success');
    setVersion((v) => v + 1);
  };

  const remove = (row) => {
    deleteStockRow(row.id);
    if (showNotification) showNotification('Pending item removed.', 'success');
    setVersion((v) => v + 1);
  };

  if (!warehouse) {
    return (
      <div className="py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
        Warehouse not found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-6" style={{ zoom: 0.85 }}>
      {/* Header */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ backgroundColor: accent }}>
              <Box size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-slate-800">Pending Stock</h2>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                {warehouse.name}
              </p>
            </div>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search pending items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-xs font-bold text-slate-700 bg-slate-50 border-none rounded-xl focus:ring-2 focus:bg-white transition-all outline-none"
              style={{ '--tw-ring-color': accent }}
            />
          </div>
        </div>
      </section>

      {/* Table Area */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex-1">
        <div className="overflow-x-auto custom-scrollbar min-h-[400px]">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Box size={48} className="mb-4 opacity-20" />
              <p className="text-sm font-bold">No pending stock found.</p>
              <p className="text-xs font-medium opacity-60">Received purchase orders will appear here.</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-100">
                  <th className="px-5 py-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Item</th>
                  <th className="px-5 py-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Quantity</th>
                  <th className="px-5 py-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Source</th>
                  <th className="px-5 py-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Low Stock Alert Qty</th>
                  {canEdit && <th className="px-5 py-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const product = products.find((p) => p.id === row.productId);
                  const pName = product ? product.name : row.productId;
                  const vLabel = variantLabelOf(product, row.variantId);
                  const isEmerg = row.isEmergencyTransfer || row.isReserved;

                  return (
                    <motion.tr
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      key={row.id}
                      className={`group hover:bg-slate-50/50 transition-colors ${isEmerg ? 'bg-red-50/30' : ''}`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex flex-col">
                          <p className="text-xs font-bold text-slate-800">{pName}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {vLabel !== 'Default' && <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide bg-slate-100 px-1.5 py-0.5 rounded">{vLabel}</span>}
                            {isEmerg && <span className="text-[10px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded uppercase tracking-wide">Emergency</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className="text-sm font-bold text-slate-700">{row.qty}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-[10px] font-bold text-slate-500 uppercase">
                          {row.note || 'Manual / Received'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {canEdit ? (
                          <div className="w-24 relative">
                            <input
                              type="number"
                              min="0"
                              value={draftAlert[row.id] ?? row.minStockThreshold ?? 0}
                              onChange={(e) => setDraftAlert({ ...draftAlert, [row.id]: e.target.value })}
                              className="w-full pl-3 pr-2 py-1.5 text-xs font-bold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition-all"
                              style={{ '--tw-ring-color': accent }}
                            />
                          </div>
                        ) : (
                          <span className="text-xs font-bold text-slate-700">{row.minStockThreshold ?? 0}</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-5 py-4 text-right">
                          <RowActions>
                            <IconAction icon={Check} tone="success" label={`Move ${row.name || 'this line'} to active stock`} title="Move to Active" onClick={() => commit(row)} />
                            <IconAction icon={Trash2} tone="danger" label={`Delete ${row.name || 'this line'}`} title="Delete" onClick={() => remove(row)} />
                          </RowActions>
                        </td>
                      )}
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
};

WhPendingStockTab.propTypes = {
  org: PropTypes.object.isRequired,
  warehouseId: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
  canEdit: PropTypes.bool,
};
